import React from 'react';
import ArticleAuthorLink from '../ArticleAuthorLink';
import Link from '@/components/Link';
import bemBlock from '@gik/core/utils/bemBlock';
import BackgroundImage from '@gik/ui/BackgroundImage';
import KeepProportions from '@gik/ui/KeepProportions';
import Article from '@gik/blog/models/Article';
import PrimaryTaxonomyItemLink from '@gik/blog/components/PrimaryTaxonomyItemLink';
import HTMLParser from '@gik/ui/HTMLParser';
import { FrameBone, ParagraphBone } from '@gik/ui/SkeletonLoader';

export const blogEndpoint = 'articles';

interface IArticleTileProps extends React.HTMLAttributes<HTMLDivElement> {
  article?: Article;
  showCategory?: boolean;
  showDescription?: boolean;
  showAuthor?: boolean;
  skeleton?: boolean;
  row?: boolean;
}

const skelBem = bemBlock(`article-tile-skeleton`);
const Skeleton = ({ showCategory, showDescription, showAuthor }: Partial<IArticleTileProps>): React.ReactElement => (
  <div className={`${skelBem()}`}>
    <KeepProportions proportion={0.72}>
      <div className={skelBem('image-wrapper')}>
        <FrameBone className={skelBem('image')} />
      </div>
    </KeepProportions>
    <main>
      {showCategory && <ParagraphBone words={2} className={skelBem('category')} />}
      <ParagraphBone words={6} className={skelBem('title')} />
      {showAuthor && <ParagraphBone words={20} className={skelBem('author-link')} />}
      {showDescription && <ParagraphBone words={20} className={skelBem('description')} />}
    </main>
  </div>
);

export default function ArticleTile({
  article,
  showCategory,
  showDescription,
  showAuthor,
  skeleton,
  className,
  row = false,
}: IArticleTileProps): React.ReactElement {
  const bem = bemBlock('article-tile');

  if (skeleton)
    return <Skeleton showCategory={showCategory} showDescription={showDescription} showAuthor={showAuthor} />;

  const articleLinkUrl = `/${blogEndpoint}/${article.articleSlug}`;

  return (
    <div className={bem(null, [{ row }], className)}>
      <Link href={articleLinkUrl}>
        <a className={bem('image-wrapper')}>
          <KeepProportions proportion={row ? 1 : 0.72}>
            <BackgroundImage src={article.smallImageUrl} className={bem('image')} />
          </KeepProportions>
        </a>
      </Link>
      <main>
        {showCategory && <PrimaryTaxonomyItemLink className={bem('category')} articleLink={article} />}

        <div className={bem('title')}>
          <Link href={articleLinkUrl}>
            <a className={bem('title')}>{article.title}</a>
          </Link>
        </div>

        {showDescription && <div className={bem('description')}>{HTMLParser({ rawHtml: article.excerpt })}</div>}
        {showAuthor && (
          <ArticleAuthorLink
            authorFullName={article.authorFullName}
            authorSlug={article.authorSlug}
            articleId={article.articleId}
            className={bem('author-link')}
          />
        )}
      </main>
    </div>
  );
}
